import { ChangeEvent, useCallback, useMemo } from 'react';
import { MenuItem } from '@material-ui/core';
import {
  WbTextField,
  useStyles,
  SelectIcon,
} from '@agilelab/plugin-wb-platform';
import { PrototypeStatus } from '@agilelab/plugin-wb-system-prototype-common';
import { StatusBadge } from './StatusBadge';
import { FieldProps } from '@rjsf/utils';
import { JSONSchema7 } from 'json-schema';
import { UseDependencyManagerResult } from '../../MultiStepJsonForm/DependencyManager';
import { extractFieldPathFromRjsfId } from '../../MultiStepJsonForm/utils/extractFieldPathFromRjsfId';

/**
 * PrototypeStatusPicker - A specialized status picker component designed specifically for SystemPrototypes.
 *
 * This component should only be used in contexts where you need to select a status for a SystemPrototype.
 * It provides a dropdown interface with visual status badges for each available PrototypeStatus value.
 *
 * @example
 * ```tsx
 * <PrototypeStatusPicker
 *   formData={currentStatus}
 *   onChange={handleStatusChange}
 *   schema={{ title: 'Prototype Status' }}
 * />
 * ```
 */
export interface PrototypeStatusPickerProps extends Omit<FieldProps, 'schema'> {
  schema: JSONSchema7 & {
    title?: string;
  };
  uiSchema?: {
    'ui:options'?: {
      label?: string;
    };
  };
}

export const PrototypeStatusPicker = ({
  formData,
  onChange,
  error,
  schema,
  disabled,
  required,
  idSchema,
  formContext,
}: PrototypeStatusPickerProps) => {
  const classes = useStyles();
  const effectiveTitle = schema?.title ?? '';

  // Extract dependency manager and field path
  const dependencyManager = formContext?.dependencyManager as
    | UseDependencyManagerResult
    | undefined;
  const fieldPath = useMemo(() => {
    const id = idSchema?.$id || '';
    return extractFieldPathFromRjsfId(id);
  }, [idSchema]);

  const handleChange = useCallback(
    (event: ChangeEvent<{ value: unknown }>) => {
      const newValue = event.target.value as string;

      // Notify dependency manager to propagate changes to dependents, or fallback to onChange
      if (dependencyManager?.notifyUpdate && fieldPath) {
        dependencyManager.notifyUpdate({
          fieldPath,
          value: newValue,
          source: 'user',
          intent: 'manual',
        });
      } else {
        onChange(newValue);
      }
    },
    [onChange, dependencyManager, fieldPath],
  );

  return (
    <WbTextField
      select
      fullWidth
      label={effectiveTitle}
      value={formData ?? ''}
      onChange={handleChange}
      error={error}
      disabled={disabled}
      required={required}
      id={idSchema?.$id}
      SelectProps={{
        IconComponent: SelectIcon,
        displayEmpty: true,
        renderValue: selected =>
          selected ? (
            <div style={{ marginLeft: -8 }}>
              <StatusBadge
                status={selected as PrototypeStatus}
                style={{ margin: 0 }}
              />
            </div>
          ) : (
            <em style={{ color: '#888' }}>Select a status</em>
          ),
        MenuProps: {
          PaperProps: {
            style: { minWidth: 200 },
          },
        },
      }}
      InputProps={{
        className: classes.autocompleteInputRoot,
        style: {
          minHeight: 45,
          background: '#fff',
          fontWeight: 500,
        },
      }}
      InputLabelProps={{
        classes: { root: classes.inputLabelRoot },
        shrink: true,
        style: { fontWeight: 600, fontSize: 12 },
      }}
      style={{ marginTop: 8, marginBottom: 8, borderRadius: 8, minHeight: 45 }}
    >
      <MenuItem value="" disabled>
        <em>Select a status</em>
      </MenuItem>
      {Object.values(PrototypeStatus).map(statusKey => (
        <MenuItem key={statusKey} value={statusKey} style={{ minHeight: 40 }}>
          <StatusBadge status={statusKey} style={{ margin: 0 }} />
        </MenuItem>
      ))}
    </WbTextField>
  );
};
